import React, { useState, useEffect } from 'react';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Calendar, User, ArrowRight, BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import { blogData } from './dummyData';

export const BlogsPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 8;

   // Scroll to top on component mount
    useEffect(() => {
      window.scrollTo(0, 0);
    }, []);

  // Sort blogs by date (newest first)
  const sortedBlogs = [...blogData].sort((a, b) => new Date(b.date) - new Date(a.date));

  // Calculate pagination
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = sortedBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(sortedBlogs.length / blogsPerPage);

  const handleReadMore = (externalLink) => {
    window.open(externalLink, '_blank');
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Navbar />
      
      <main className="pt-20">
        {/* Header */}
        <section className="py-20 bg-gradient-to-r from-[hsl(var(--primary))]/5 to-[hsl(var(--secondary))]/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center space-x-2 bg-[hsl(var(--primary))]/10 px-4 py-2 rounded-full mb-6">
              <BookOpen className="w-5 h-5 text-[hsl(var(--primary))]" />
              <span className="text-sm font-semibold text-[hsl(var(--primary))]">
                Health Blog
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[hsl(var(--foreground))] mb-6">
              Medical Insights &
              <span className="block mt-2 text-gradient">Health Articles</span>
            </h1>
            <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
              Stay informed with the latest medical research, health tips, and expert advice from our healthcare professionals.
            </p>
          </div>
        </section>

        {/* Blogs Grid */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
              {currentBlogs.map((blog) => (
                <Card key={blog.id} className="group h-full hover:shadow-[var(--shadow-xl)] transition-all duration-500 hover:-translate-y-2 border-2 border-transparent hover:border-[hsl(var(--primary))]/20">
                  <CardContent className="p-6 space-y-4 h-full flex flex-col">
                    {/* Meta Information */}
                    <div className="flex items-center justify-between text-sm text-[hsl(var(--muted-foreground))]">
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4" />
                        <span>{blog.author}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(blog.date).toLocaleDateString()}</span>
                      </div>
                    </div>

                    {/* Blog Title */}
                    <CardTitle className="text-xl font-bold text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--primary))] transition-colors line-clamp-2">
                      {blog.title}
                    </CardTitle>

                    {/* Blog Excerpt */}
                    <p className="text-[hsl(var(--muted-foreground))] leading-relaxed line-clamp-3 flex-grow">
                      {blog.excerpt}
                    </p>

                    {/* Read Time and Button */}
                    <div className="flex items-center justify-between pt-4 border-t border-[hsl(var(--border))]">
                      <span className="text-sm text-[hsl(var(--primary))] font-medium">
                        {blog.readTime}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReadMore(blog.externalLink)}
                        className="group font-semibold"
                      >
                        Read More
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mt-16">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex items-center space-x-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Previous</span>
                </Button>

                {[...Array(totalPages)].map((_, index) => (
                  <Button
                    key={index + 1}
                    variant={currentPage === index + 1 ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(index + 1)}
                    className="min-w-[40px]"
                  >
                    {index + 1}
                  </Button>
                ))}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex items-center space-x-2"
                >
                  <span>Next</span>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};